/**
 * MapLibreMap — react-map-gl v8 + maplibre-gl wrapper for SIGNAL signal pins.
 *
 * Uses the free CARTO dark-matter raster tile source — no Mapbox token
 * required. Renders a list of `pins = [{ id, lat, lon, label, severity }]`
 * as severity-coloured circles.
 *
 * Example:
 *   <MapLibreMap
 *     pins={[{ id, lat, lon, severity: 'elevated', label: 'NDBC 41009' }]}
 *     initialView={{ longitude: -77.3, latitude: 25.0, zoom: 3 }}
 *     height={440}
 *   />
 */
import React, { useMemo } from 'react';
// `react-map-gl/maplibre` gives us the MapLibre-flavoured components without
// pulling in Mapbox GL.
import { Map, Marker, NavigationControl, AttributionControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const SEVERITY_COLOR = {
  nominal: '#7ecfc0',
  elevated: '#e8c878',
  storm: '#e8a598',
  critical: '#e87878',
  offline: '#6b7280',
};

// Free CARTO dark basemap — public, no key, CORS-enabled.
const DEFAULT_STYLE = {
  version: 8,
  sources: {
    'carto-dark': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap · © CARTO',
    },
  },
  layers: [{ id: 'carto-dark', type: 'raster', source: 'carto-dark' }],
};

export default function MapLibreMap({
  pins = [],
  initialView = { longitude: -77.35, latitude: 25.06, zoom: 3 },
  height = 440,
  testId = 'maplibre-map',
  onPinClick,
}) {
  const markers = useMemo(
    () =>
      pins
        .filter((p) => typeof p.lat === 'number' && typeof p.lon === 'number')
        .map((p) => (
          <Marker
            key={p.id}
            longitude={p.lon}
            latitude={p.lat}
            anchor="center"
            onClick={onPinClick ? (e) => { e.originalEvent.stopPropagation(); onPinClick(p); } : undefined}
          >
            <div
              data-testid={`maplibre-pin-${p.id}`}
              title={p.label || p.id}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: SEVERITY_COLOR[p.severity] || SEVERITY_COLOR.nominal,
                boxShadow: `0 0 10px ${SEVERITY_COLOR[p.severity] || SEVERITY_COLOR.nominal}`,
                border: '1px solid rgba(0, 0, 0, 0.5)',
                cursor: onPinClick ? 'pointer' : 'default',
              }}
            />
          </Marker>
        )),
    [pins, onPinClick]
  );

  return (
    <div
      data-testid={testId}
      style={{ width: '100%', height, position: 'relative', borderRadius: 6, overflow: 'hidden' }}
    >
      <Map
        initialViewState={initialView}
        mapStyle={DEFAULT_STYLE}
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
      >
        <NavigationControl position="top-right" />
        <AttributionControl compact position="bottom-right" />
        {markers}
      </Map>
    </div>
  );
}
